import { ConfirmationDialog } from "@/common/modal/userCancelActionWarning";
import Button from "@/common/ui/Button";
import { DefaultErrorBoundary } from "@/common/ui/error/Error";
import { RoundLoader } from "@/common/ui/Loader";
import { PermissionDenied } from "@/common/ui/page/PermissionDeniedPage";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { InfoText } from "@/common/ui/Text";
import EmptyList from "@/components/common/EmptyList";

import AddSignatoryModal from "@/components/dashboard/setting/signatoryAndLetterHead/AddSignatoryModal";
import TemplatePreview from "@/components/dashboard/setting/signatoryAndLetterHead/TemplatePreview";
import { FormRenderRef } from "@/components/Form/Renderer/RenderFormWithRef";
import RenderFormWithRefAndChildren from "@/components/Form/Renderer/RenderFormWithRefAndChildren";
import RenderLabel from "@/components/Form/Renderer/RenderLabel";
import RenderWidgets from "@/components/Form/Renderer/RenderWigets";
import { useDebounce } from "@/hooks/useDebounce";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { addUpdateLetterHead, useSignatoryAndLetterHeadInfo } from "@/services/common/profile.services";
import { dashboardSelector } from "@/store/features/dashboardSlice";
import { isOrgPending, loginSelector } from "@/store/features/loginSlice";
import { useCountries } from "@/store/hooks/useCountries";
import { MODE } from "@/types/global";
import { CANCEL_DIRTY_FORM } from "@/utils/constant/dashboard.constant";
import { LetterHeadFormConfig } from "@/utils/constant/formConfig.constant";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { getNestedValue, removePreviewUrl } from "@/utils/helper/form";
import { toastMessage } from "@/utils/helper/myToast";
import { isHasPermission } from "@/utils/helper/permissions";
import { requestUserConfirmation } from "@/utils/helper/promise";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const modalConfig: ModalConfig = {
  addSignatory: {
    component: AddSignatoryModal,
    title: "Upload authorization document",
    type: "side",
  },
  closeWarningModal: {
    component: ConfirmationDialog,
    title: "Discard changes",
    modalClassName: "w-[330px]",
  },
};

interface LetterHeadProps {
  openModal: (modalKey: string, props?: any) => void;
  activeModal: string;
  closeModal: () => void;
}

const watchFiledList = ["templateId", "logo", "phone", "address", "phNo", "phCountryCode", "email", "signatory", "cmpName"];

const LetterHeadPage: React.FC<LetterHeadProps> = ({ closeModal, openModal }) => {
  const [mode, setMode] = useState<MODE>("view");
  const isOrgPendingStatus = useSelector(isOrgPending);
  const { currentUser } = useSelector(loginSelector);
  const { data, isLoading, refetch }: any = useSignatoryAndLetterHeadInfo({ key: "letter" });
  const { isLoading: countryIsLoading } = useCountries({ autoFetch: true });
  const { action } = useSelector(dashboardSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef: any = useRef<FormRenderRef>(null);
  const [watchFields, setWatchFields] = useState<any>(null);

  const debouncedSubmit = useDebounce(async () => {
    if (!formRef.current) return;
    try {
      await formRef.current.submitForm();
    } catch (error) {
      console.error("Form submission failed:", error);
    }
  }, 300);

  const handleSubmit = () => {
    debouncedSubmit();
  };
  const handleUpdate = async (data: any) => {
    const payload = removePreviewUrl(data, ["logo", "signatory"]);
    const { phCid, phCountryCode, phNo, ...rest } = payload;
    const newPayload: any = { ...rest };
    newPayload.phone = {
      cid: phCid,
      country_phone_code: phCountryCode,
      phoneNo: phNo,
      phone: `+${phCountryCode}-${phNo}`,
    };
    try {
      setIsSubmitting(true);
      const res: any = await addUpdateLetterHead(newPayload);
      if (res?.status === 200) {
        const { message, isError, showMessage } = res.data;
        setMode("view");
        return showMessage && toastMessage(message, isError);
      } else {
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChangeViewMode = async () => {
    if (action && action === "FORM_FILL") {
      try {
        await requestUserConfirmation({
          openModal,
          closeModal,
          modalKey: "closeWarningModal",
          modalProps: CANCEL_DIRTY_FORM,
        });
        closeModal();
        setMode("view");
      } catch (err) {
        console.log(err);
      }
    } else {
      setMode("view");
    }
  };

  useEffect(() => {
    if (mode === "view") {
      refetch();
    }
  }, [mode]);

  if (!isHasPermission(currentUser?.perms, PERMISSIONS.SETTING["View Letterhead"])) {
    return <PermissionDenied />;
  }
  if (isLoading || countryIsLoading) return <RoundLoader />;

  return (
    <div className="px-2 md:px-4 flex flex-col flex-1 h-full">
      <SideBarContentHeader heading="Letterhead">
        {mode === "edit" ? (
          <div className=" flex gap-x-4">
            <Button variant="secondary" size="small" onClick={handleChangeViewMode}>
              Cancel
            </Button>
            <Button variant="primary" size="small" onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting}>
              Save
            </Button>
          </div>
        ) : (
          <>
            <Button
              variant="primary"
              size="small"
              onClick={() => setMode("edit")}
              disabled={isOrgPendingStatus || !isHasPermission(currentUser?.perms, PERMISSIONS.SETTING["Edit Letterhead"])}
            >
              Edit
            </Button>
          </>
        )}
      </SideBarContentHeader>
      <DefaultErrorBoundary>
        {mode === "edit" ? (
          <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-4 w-full h-full py-5">
            <RenderFormWithRefAndChildren
              handleFormSubmit={handleUpdate}
              watchFieldList={watchFiledList}
              setWatchField={setWatchFields}
              className="h-full md:w-3/5 md:max-w-[700px]"
              ref={formRef}
            >
              {LetterHeadFormConfig.sections.map((section: any, sectionIndex: number) => (
                <div className="flex gap-6 w-full" key={sectionIndex}>
                  {section.widgets?.map((widget: any) => {
                    const defaultValue = widget.apiField ? getNestedValue(data?.letter, widget.apiField) : undefined;
                    let defaultOptions = undefined;

                    if (widget.field === "templateId") {
                      defaultOptions = data?.templateOptions?.map((o: any) => ({ label: o.key, value: o.id }));
                    }
                    return (
                      <div className={`flex flex-col gap-1 gap-y-2 w-full ${widget.className || "xl:min-w-formField xl:max-w-formField"}`} key={widget.field}>
                        {LetterHeadFormConfig?.showLabel && !widget?.hideLabel && (
                          <RenderLabel
                            label={widget.label}
                            required={widget?.validation?.required}
                            labelInfo={widget?.labelInfo}
                            className="text-sm text-gray-600"
                          />
                        )}
                        <RenderWidgets widget={widget} defaultValue={defaultValue} optionsFromAPI={defaultOptions} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </RenderFormWithRefAndChildren>
            <TemplatePreview
              templateId={watchFields?.templateId}
              templateOptions={data?.templateOptions}
              cmpName={watchFields?.cmpName}
              address={watchFields?.address}
              email={watchFields?.email}
              phone={`+${watchFields?.phCountryCode}-${watchFields?.phNo}`}
              logo={watchFields?.logo?.previewUrl || "/icons/logo.svg"}
              sealAndSignature={watchFields?.signatory?.previewUrl}
              setValueRef={formRef?.current?.setValue}
            />
          </div>
        ) : (
          <>
            {isOrgPendingStatus ? (
              <InfoText text="You can’t update letterhead until your document is verified" className="mt-4" />
            ) : !data?.letter?.letter_head?.logo?.previewUrl ? (
              <InfoText text="You can’t share employee document until you completed Letterhead." className="mt-4" />
            ) : null}

            {data?.templateOptions ? (
              <div className="grid grid-cols-1 llg:grid-cols-2 1.5xl:grid-cols-3 gap-2 min-h-[450px] w-full md:w-[calc(100%-20px)] overflow-x-auto">
                {data.templateOptions.map((template: any) => {
                  const { address, email, phone, logo, signatory } = data?.letter?.letter_head || {};
                  const { cmp_name } = data?.letter;
                  const isActive = data?.letter?.letter_head_template_id === template?.id;
                  return (
                    <TemplatePreview
                      key={template.id}
                      templateId={template.id}
                      templateOptions={data?.templateOptions}
                      cmpName={cmp_name}
                      address={address}
                      email={email}
                      phone={phone?.phone}
                      logo={logo?.previewUrl || "/icons/logo.svg"}
                      isViewMode
                      isActive={isActive}
                      sealAndSignature={signatory?.previewUrl}
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyList
                text="No letterhead available. Please get your account approved to access this feature."
                icon="/icons/document.svg"
                className="h-full gap-y-4"
                textClassName="text-center"
              />
            )}
          </>
        )}
      </DefaultErrorBoundary>
    </div>
  );
};

export const LetterHeadPageWithModal = withModal(LetterHeadPage, modalConfig);
